/**
 * Scroll the canvas so the day the panel just opened is not underneath it.
 *
 * ── The gap this closes ─────────────────────────────────────────────────────────────────────
 * The 574px panel OVERLAYS the canvas and the canvas scrolls sideways, so opening the last training
 * day of a full week leaves `scrollLeft` at 0 and the card entirely behind the panel — its selection
 * ring included. The mock has one scroll position and therefore no opinion; the owner asked for this
 * on 7 September 2026.
 *
 * **Both canvases owe it, and the sequence one owes it harder.** A cycle is a row of as many columns
 * as the phase has trainings and gaps, where a week is seven; on a long cycle the panel opens over
 * the card the trainer just clicked and the new selection ring is invisible for the one day they
 * opened. The attribute the scrollport is found by is therefore CANVAS-neutral
 * (`data-canvas-scroller`), stamped by `WeekPane` and `TapePane` alike, and each canvas component
 * mounts this hook for itself.
 *
 * ── Why `offsetWidth` and never a rect ──────────────────────────────────────────────────────
 * This is the load-bearing detail, and the next reader will want to "simplify" it. The panel spends
 * `--dur-panel` (340ms) mid-`translateX` after it opens, so `getBoundingClientRect()` during that
 * window reports an INTERMEDIATE x — the measurement comes out short and the day lands half covered.
 * `offsetWidth` is layout width: a transform does not touch it, so the answer is right on the first
 * frame and there is no `transitionend` to wait for or to race.
 *
 * ── What it deliberately does not do ────────────────────────────────────────────────────────
 * It does not listen for scroll. A trainer dragging the scrollbar while the panel is open is looking
 * at another day on purpose, and a hook that pulled the view back would be fighting them. It runs on
 * exactly two changes: which day is open, and the dock becoming the panel. In fullscreen there is no
 * canvas on screen to reveal anything on.
 *
 * ── It measures on the NEXT frame, and that is a fact about Base UI rather than a delay ──────
 * `Dialog.Portal` resolves its container in a layout effect and renders the popup on a SECOND pass,
 * so at the moment this canvas's own effect runs — sibling effects go in tree order, and the canvas
 * is rendered before the dock — `[data-day-panel]` is not in the document yet. Measured: without the
 * frame the query came back `null` and the scroll never happened at all. One `requestAnimationFrame`
 * is the wait for that mount, cancelled on cleanup; it is not a delay hiding a symptom, and it is
 * not a race — React commits the second pass before the browser paints, so the panel is there by the
 * time the callback runs, and `offsetWidth` is right on that frame whatever the transform is doing.
 *
 * ── The two nodes are found by attribute ────────────────────────────────────────────────────
 * Each pane marks its scrollport, and `DayColumn` and `TapePane` already stamp every column with
 * its day's slot token. The panel marks itself, because Base UI portals it into `<body>` and no ref could reach it
 * from here. Same shape as `pages/assigned-plan-calendar/lib/useColumnsUnderOverlay.ts`, which
 * answers the same kind of question about the same kind of overlay.
 */

import { useEffect } from 'react';

import { revealScrollDelta } from '../model/revealDay';

import type { DayDockMode } from './usePlanBuilderSearch';

const SCROLLER = '[data-canvas-scroller]';
const PANEL = '[data-day-panel]';

/**
 * The breathing room left between the revealed column and the panel's edge. Enough that the card
 * reads as standing beside the panel rather than jammed against it.
 */
const GUTTER = 12;

export interface RevealOpenDayInput {
  /** The day the dock is open on, or `null` when it is closed. */
  readonly daySlotToken: string | null;
  readonly dock: DayDockMode | null;
  /**
   * The viewer's motion preference, passed IN.
   *
   * The single `prefers-reduced-motion` block in `tokens/base.css` cannot reach a scripted scroll,
   * so a surface that relied on it would ignore the preference while looking compliant.
   */
  readonly reducedMotion: boolean;
}

export function useRevealOpenDay({ daySlotToken, dock, reducedMotion }: RevealOpenDayInput): void {
  useEffect(() => {
    if (dock !== 'panel' || daySlotToken === null) return;

    const frame = requestAnimationFrame(() => {
      const scroller = document.querySelector<HTMLElement>(SCROLLER);
      const panel = document.querySelector<HTMLElement>(PANEL);
      const column = scroller?.querySelector<HTMLElement>(
        `[data-day-slot="${CSS.escape(daySlotToken)}"]`,
      );
      if (scroller == null || panel == null || column == null) return;

      const scrollport = scroller.getBoundingClientRect();
      const box = column.getBoundingClientRect();
      const delta = revealScrollDelta({
        scrollerLeft: scrollport.left,
        scrollerRight: scrollport.right,
        columnLeft: box.left,
        columnRight: box.right,
        // From the panel's layout left edge to the scrollport's end, immune to the transform the
        // panel is in the middle of (see the header): the panel alone, or the panel and the Buddy
        // panel to its right when both are open (owner, 7 October 2026).
        obstructionWidth: Math.max(panel.offsetWidth, scrollport.right - panel.offsetLeft),
        gutter: GUTTER,
      });
      if (delta === 0) return;

      scroller.scrollBy({ left: delta, behavior: reducedMotion ? 'auto' : 'smooth' });
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [daySlotToken, dock, reducedMotion]);
}
